import React, { useState } from 'react';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import api, { formatApiError } from '../../api';
import { SettingsSection } from './SettingsLayout';
import { Field2 } from './Field2';
import { useSettings } from './useSettings';
import { useAuth } from '../../contexts/AuthContext';

export default function CompanySettings() {
    const { settings, update } = useSettings();
    const { can, user } = useAuth();
    const readOnly = !can('settings', 'edit');
    const [testEmailTo, setTestEmailTo] = useState('');
    const [testingEmail, setTestingEmail] = useState(false);

    const sendTestEmail = async () => {
        const to = (testEmailTo || user?.email || '').trim();
        if (!to) return toast.error('Enter an email address to send the test to.');
        setTestingEmail(true);
        try {
            const r = await api.post('/support/test-email', { to });
            toast.success(`Sent from ${r.data.sent_from || 'connected mailbox'} to ${to}. Check the inbox.`);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail), { duration: 8000 });
        } finally {
            setTestingEmail(false);
        }
    };

    if (!settings) return <div className="text-[color:var(--bm-text-muted)]">Loading…</div>;

    return (
        <fieldset disabled={readOnly} className={readOnly ? 'opacity-70 pointer-events-none' : ''}>
            <SettingsSection title="Company details" testId="company-details-card">
                <Field2 label="Company name" value={settings.company_name} onChange={v => update('company_name', v)} testId="company-name-input" />
                <Field2 label="Your VAT / Tax number" value={settings.company_vat} onChange={v => update('company_vat', v)} testId="company-vat-input" />
                <div className="sm:col-span-2">
                    <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Registered address</Label>
                    <Textarea value={settings.company_address || ''} onChange={e => update('company_address', e.target.value)} className="mt-1.5" rows={2} data-testid="company-address-input" />
                </div>
                <div className="sm:col-span-2">
                    <Field2 label="Logo URL (horizontal)" value={settings.company_logo_url} onChange={v => update('company_logo_url', v)} testId="company-logo-input" />
                </div>
            </SettingsSection>

            <SettingsSection title="Defaults" description="Financial and operational defaults applied to new proposals, invoices and contracts.">
                <Field2 label="Default VAT rate (%)" value={settings.default_vat_rate} onChange={v => update('default_vat_rate', v)} type="number" />
                <Field2 label="Default payment terms (days)" value={settings.default_payment_terms} onChange={v => update('default_payment_terms', v)} type="number" />
                <Field2 label="Payment link (Monzo etc.)" value={settings.payment_link_default} onChange={v => update('payment_link_default', v)} placeholder="https://monzo.me/..." />
            </SettingsSection>

            <SettingsSection title="Bank transfer details" description="Shown on every invoice PDF & email under 'Pay by bank transfer', so clients can pay manually without a link.">
                <Field2 label="Account name" value={settings.bank_account_name} onChange={v => update('bank_account_name', v)} placeholder="Beyond Measure Ltd" testId="bank-account-name-input" />
                <Field2 label="Sort code" value={settings.bank_sort_code} onChange={v => update('bank_sort_code', v)} placeholder="00-00-00" testId="bank-sort-code-input" />
                <Field2 label="Account number" value={settings.bank_account_number} onChange={v => update('bank_account_number', v)} placeholder="12345678" testId="bank-account-number-input" />
            </SettingsSection>

            <SettingsSection title="Crunch export" description="Used by the 'Export to Crunch' CSV on the Invoices page. This must match a bank account name exactly as configured in Crunch.">
                <Field2 label="Crunch bank account name" value={settings.crunch_bank_account} onChange={v => update('crunch_bank_account', v)} placeholder="Business Bank Account" testId="crunch-bank-account-input" />
            </SettingsSection>

            <SettingsSection title="Organisation & VAT">
                <div>
                    <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Organisation type</Label>
                    <select
                        value={settings.org_type || 'sole_trader'}
                        onChange={e => update('org_type', e.target.value)}
                        className="mt-1.5 h-10 w-full rounded-md border border-[color:var(--bm-border)] bg-white px-3 text-[13px]"
                        data-testid="settings-org-type"
                    >
                        <option value="sole_trader">Sole Trader</option>
                        <option value="limited">Limited Company</option>
                    </select>
                    <div className="text-[11px] text-[color:var(--bm-text-subtle)] mt-1">Drives Chart of Accounts (drawings vs Director's Loan / PAYE).</div>
                </div>
                <div>
                    <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">VAT registered</Label>
                    <label className="mt-1.5 flex items-center gap-2 h-10 px-3 rounded-md border border-[color:var(--bm-border)] bg-white text-[13px]">
                        <input type="checkbox" checked={!!settings.vat_registered} onChange={e => update('vat_registered', e.target.checked)} data-testid="settings-vat-registered" />
                        Yes — apply VAT input/output postings
                    </label>
                </div>
                {!!settings.vat_registered && (
                    <>
                        <div>
                            <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">VAT scheme</Label>
                            <select
                                value={settings.vat_scheme || 'standard'}
                                onChange={e => update('vat_scheme', e.target.value)}
                                className="mt-1.5 h-10 w-full rounded-md border border-[color:var(--bm-border)] bg-white px-3 text-[13px]"
                            >
                                <option value="standard">Standard accruals</option>
                                <option value="cash">Cash accounting</option>
                                <option value="flat_rate">Flat rate scheme</option>
                            </select>
                        </div>
                        {settings.vat_scheme === 'flat_rate' && (
                            <Field2 label="Flat rate (%)" value={settings.vat_flat_rate} onChange={v => update('vat_flat_rate', v)} type="number" />
                        )}
                    </>
                )}
                <div className="sm:col-span-2">
                    <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">General Ledger auto-posting</Label>
                    <label className="mt-1.5 flex items-center gap-2 text-[13px]">
                        <input type="checkbox" checked={!!settings.gl_enabled} onChange={e => update('gl_enabled', e.target.checked)} data-testid="settings-gl-enabled" />
                        Enable double-entry GL postings (invoices, payments, categorised expenses)
                    </label>
                </div>
            </SettingsSection>

            <SettingsSection title="Document numbering">
                <Field2 label="Proposal prefix" value={settings.proposal_number_prefix} onChange={v => update('proposal_number_prefix', v)} />
                <Field2 label="Contract prefix" value={settings.contract_number_prefix} onChange={v => update('contract_number_prefix', v)} />
                <Field2 label="Invoice prefix" value={settings.invoice_number_prefix} onChange={v => update('invoice_number_prefix', v)} />
            </SettingsSection>

            <SettingsSection title="Project stage SLA targets (days)" wide>
                <div className="sm:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                        ['pre_sales',       'Pre-sales'],
                        ['proposal_sent',   'Proposal sent'],
                        ['contract_signed', 'Contract signed'],
                        ['in_build',        'In build'],
                        ['client_review',   'Client review'],
                        ['handover',        'Handover'],
                        ['live',            'Live'],
                        ['renewal_due',     'Renewal due'],
                    ].map(([key, label]) => (
                        <div key={key}>
                            <Label className="text-[11.5px] font-medium text-[color:var(--bm-text-muted)]">{label}</Label>
                            <Input
                                type="number"
                                value={(settings.project_stage_sla || {})[key] ?? ''}
                                onChange={e => update('project_stage_sla', { ...(settings.project_stage_sla || {}), [key]: parseInt(e.target.value) || -1 })}
                                className="mt-1 h-9"
                                placeholder="-1 = no SLA"
                                data-testid={`sla-${key}`}
                            />
                        </div>
                    ))}
                </div>
            </SettingsSection>

            <SettingsSection title="Hosting / Domain defaults">
                <Field2 label="BM subdomain suffix" value={settings.hosting_subdomain_suffix} onChange={v => update('hosting_subdomain_suffix', v)} placeholder="beyondmeasure.global" />
                <Field2 label="Default domain setup fee (£)" value={settings.default_domain_setup_fee} onChange={v => update('default_domain_setup_fee', v)} type="number" />
                <Field2 label="Default ongoing domain support (£/mo)" value={settings.default_domain_monthly_support_fee} onChange={v => update('default_domain_monthly_support_fee', v)} type="number" />
                <div className="sm:col-span-2">
                    <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Proposal wording — BM subdomain option</Label>
                    <Textarea value={settings.hosting_blurb_subdomain || ''} onChange={e => update('hosting_blurb_subdomain', e.target.value)} rows={3} className="mt-1.5" data-testid="settings-blurb-subdomain" />
                </div>
                <div className="sm:col-span-2">
                    <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Proposal wording — Client-owned domain option</Label>
                    <Textarea value={settings.hosting_blurb_client_domain || ''} onChange={e => update('hosting_blurb_client_domain', e.target.value)} rows={3} className="mt-1.5" data-testid="settings-blurb-client-domain" />
                </div>
            </SettingsSection>

            <SettingsSection title="Support Hub" description="Client Support Hub ticket emails (confirmations, approvals, status updates) and response-time targets.">
                <Field2 label="Send emails from" value={settings.support_from_email} onChange={v => update('support_from_email', v)} placeholder="support@beyondmeasure.global" testId="support-from-email-input" />
                <div className="sm:col-span-2 text-[11px] text-[color:var(--bm-text-subtle)]">
                    Requires the connected Microsoft account to have <strong>"Send As"</strong> (not "Send on Behalf of") delegated permission on this shared mailbox in Microsoft 365 (Exchange admin center). Leave blank to send from the connected admin's own mailbox. <strong>Save this field first</strong>, then use the test button below to confirm it's actually working — real ticket emails fail silently, this one shows you the exact error.
                </div>
                <div className="sm:col-span-2 flex items-end gap-2">
                    <div className="flex-1">
                        <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Send test email to</Label>
                        <Input value={testEmailTo} onChange={e => setTestEmailTo(e.target.value)} placeholder={user?.email || 'you@beyondmeasure.global'}
                               className="mt-1.5 h-10" data-testid="support-test-email-input" />
                    </div>
                    <Button type="button" variant="outline" onClick={sendTestEmail} disabled={testingEmail} className="h-10" data-testid="support-test-email-btn">
                        {testingEmail ? 'Sending…' : 'Send test email'}
                    </Button>
                </div>
                <div className="sm:col-span-2">
                    <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Notify internal team on new request (comma-separated emails)</Label>
                    <Input
                        value={Array.isArray(settings.support_internal_notify_emails) ? settings.support_internal_notify_emails.join(', ') : (settings.support_internal_notify_emails || '')}
                        onChange={e => update('support_internal_notify_emails', e.target.value)}
                        className="mt-1.5 h-10"
                        placeholder="fabio@beyondmeasure.global"
                        data-testid="settings-support-internal-notify-emails"
                    />
                </div>
                <div className="sm:col-span-2">
                    <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">First-response SLA targets (hours, by priority)</Label>
                    <div className="mt-1.5 grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {['critical', 'high', 'medium', 'low'].map(p => (
                            <div key={p}>
                                <label className="text-[10.5px] text-[color:var(--bm-text-subtle)] capitalize">{p}</label>
                                <Input type="number" min={1}
                                       value={(settings.support_sla_hours || {})[p] ?? ''}
                                       onChange={e => update('support_sla_hours', { ...(settings.support_sla_hours || {}), [p]: parseFloat(e.target.value) || 0 })}
                                       className="h-9 mt-0.5" data-testid={`settings-support-sla-${p}`} />
                            </div>
                        ))}
                    </div>
                </div>
            </SettingsSection>

            <SettingsSection title="Notifications" description="In-app notifications always work. Emails require a configured sender (Microsoft Graph).">
                <div className="sm:col-span-2">
                    <label className="flex items-center gap-2 text-[13px] font-medium">
                        <input type="checkbox" checked={!!settings.notification_email_enabled} onChange={e => update('notification_email_enabled', e.target.checked)} data-testid="settings-email-enabled" />
                        Email notifications enabled
                    </label>
                </div>
                <Field2 label="Email sender method" value={settings.notification_email_sender_method || 'deferred'} onChange={v => update('notification_email_sender_method', v)} placeholder="deferred | ms_graph" />
                <div className="sm:col-span-2">
                    <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Recipients (comma-separated emails)</Label>
                    <Input
                        value={Array.isArray(settings.notification_email_recipients) ? settings.notification_email_recipients.join(', ') : (settings.notification_email_recipients || '')}
                        onChange={e => update('notification_email_recipients', e.target.value)}
                        className="mt-1.5 h-10"
                        placeholder="fabio@beyondmeasure.global, ops@beyondmeasure.global"
                        data-testid="settings-email-recipients"
                    />
                </div>
                <div className="sm:col-span-2">
                    <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Events to notify on (comma-separated)</Label>
                    <Input
                        value={Array.isArray(settings.notification_events) ? settings.notification_events.join(', ') : (settings.notification_events || '')}
                        onChange={e => update('notification_events', e.target.value)}
                        className="mt-1.5 h-10"
                        placeholder="proposal_viewed, proposal_accepted, contract_created, ..."
                        data-testid="settings-notif-events"
                    />
                </div>
            </SettingsSection>
        </fieldset>
    );
}
